import { api, formatError } from '../../api-client'
import { analyticsEvent, flushAndExit } from '../../analytics'
import { parseArgs } from './args'
import { emitJSON } from './render'
import { levenshtein } from './flags'
import { resolveWorkspaceSlug } from '../context'

// `driftless context comment` — thin annotations on a spine object (a topic, a
// page, a record) or a project card. A comment is NOT a topic: it points AT one,
// resolves into an edit, and carries no governance of its own. Author is human
// or agent. Lives in CLI + MCP first; the dashboard is occasional visibility.

const KNOWN_COMMENT_FLAGS: ReadonlySet<string> = new Set([
  'body', 'field', 'card', 'record', 'page', 'topic-id', 'status', 'wont-fix', 'author', 'json', 'help',
])

function assertKnownCommentFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_COMMENT_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_COMMENT_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless context comment':\n` +
      lines.join('\n') +
      `\nRun 'driftless context comment --help' for valid flags.`,
  )
}

export const COMMENT_HELP = `driftless context comment <add|list|resolve|reopen> [args] [flags]

Leave a comment ON something — a topic, a record, or a project card. A comment
points at the object (and optionally a field); it resolves into an edit. Author
is you (human) or your agent. Comments appear on pull/refresh (async, no realtime).

  context comment add <topic-slug> --body "..." [--field <key>]   Comment on a topic (by slug)
  context comment add --card <uuid> --body "..."                  Comment on a project card
  context comment add --record <uuid> --body "..."               Comment on a record
  context comment list <topic-slug> [--status open]              List a topic's comments
  context comment list --card <uuid> [--status open|resolved|wont_fix]
  context comment resolve <comment-id> [--wont-fix]              Resolve (or won't-fix) a comment
  context comment reopen <comment-id>                            Reopen a resolved comment

Flags:
  --body "<text>"   The comment text (required for add).
  --field <key>     Optional — the field the comment is about (e.g. decisions), for the Note→Knowledge gate.
  --status <s>      Filter list by open | resolved | wont_fix.
  --json            Machine-readable output.

Examples:
  driftless context comment add billing-flow --body "This decision contradicts the refund invariant" --field decisions
  driftless context comment list billing-flow --status open
  driftless context comment resolve 2ec44578-...
`

// Resolve which object the comment targets from positional + flags. A bare
// positional is a topic slug (the ergonomic default — the killer placement);
// --card/--record/--page take a uuid; --topic-id takes a topic uuid.
function resolveTarget(
  positional: string[],
  flags: Record<string, string | boolean>,
): { target_kind: string; target_id?: string; target_ref?: string } | null {
  const str = (k: string) => (typeof flags[k] === 'string' ? (flags[k] as string).trim() : '')
  if (str('card')) return { target_kind: 'card', target_id: str('card') }
  if (str('record')) return { target_kind: 'record', target_id: str('record') }
  if (str('page')) return { target_kind: 'page', target_id: str('page') }
  if (str('topic-id')) return { target_kind: 'topic', target_id: str('topic-id') }
  if (positional[1]) return { target_kind: 'topic', target_ref: positional[1] } // bare slug = topic
  return null
}

export async function commentCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help' || sub === undefined) {
    process.stdout.write(COMMENT_HELP)
    process.exit(sub === undefined ? 1 : 0)
  }

  assertKnownCommentFlags(flags)
  const ws = await resolveWorkspaceSlug()

  try {
    if (sub === 'add') {
      const body = typeof flags['body'] === 'string' ? (flags['body'] as string) : ''
      if (!body.trim()) { console.error('Error: --body is required for `comment add`.'); process.exit(1) }
      const target = resolveTarget(positional, flags)
      if (!target) {
        console.error('Error: name a target — a <topic-slug>, or --card/--record/--page <uuid>.')
        process.exit(1)
      }
      const payload: Record<string, unknown> = { ...target, body }
      if (typeof flags['field'] === 'string') payload['field_key'] = flags['field']
      if (typeof flags['author'] === 'string') payload['author_id'] = flags['author']
      const created = await api.post(`/workspaces/${ws}/comments`, payload)
      analyticsEvent('context_comment_add', ws, { target_kind: target.target_kind, has_field: !!flags['field'] })
      if (isJSON) emitJSON(created)
      else console.log(`Comment added on ${target.target_kind} ${target.target_ref ?? target.target_id}.`)
      await flushAndExit(0)
    }

    if (sub === 'list') {
      const target = resolveTarget(positional, flags)
      const q = new URLSearchParams()
      if (target?.target_kind) q.set('target_kind', target.target_kind)
      if (target?.target_id) q.set('target_id', target.target_id)
      // A topic addressed by slug is filtered server-side via target_ref (resolved
      // to its uuid there) — so `comment list <slug>` shows exactly that note's feedback.
      if (target?.target_ref) q.set('target_ref', target.target_ref)
      if (typeof flags['status'] === 'string') q.set('status', flags['status'] as string)
      const rows = (await api.get(`/workspaces/${ws}/comments?${q.toString()}`)) as Array<Record<string, unknown>>
      if (isJSON) { emitJSON(rows); await flushAndExit(0) }
      if (!rows.length) console.log('No comments.')
      else for (const c of rows) {
        const mark = c['status'] === 'open' ? '○' : c['status'] === 'resolved' ? '✓' : '✗'
        const field = c['field_key'] ? ` [${c['field_key']}]` : ''
        console.log(`${mark} ${c['author_kind']}:${c['author_id']}${field} — ${c['body']}  (${c['id']})`)
      }
      await flushAndExit(0)
    }

    if (sub === 'resolve' || sub === 'reopen') {
      const id = positional[1]
      if (!id) { console.error(`Error: \`comment ${sub}\` needs a <comment-id>.`); process.exit(1) }
      const status = sub === 'reopen' ? 'open' : flags['wont-fix'] ? 'wont_fix' : 'resolved'
      const updated = await api.patch(`/workspaces/${ws}/comments/${encodeURIComponent(id)}`, { status })
      analyticsEvent('context_comment_resolve', ws, { status })
      if (isJSON) emitJSON(updated)
      else console.log(`Comment ${id} → ${status}.`)
      await flushAndExit(0)
    }

    console.error(`Unknown comment subcommand: '${sub}'. Try add | list | resolve | reopen.`)
    process.exit(1)
  } catch (e: any) {
    console.error(`Comment ${sub} failed: ${formatError(e)}`)
    await flushAndExit(1)
  }
}
